import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  ExtendedProgramGuideService,
  IInputEpisode,
  EpisodeScheduleType,
  RecurrenceType,
  EpisodeSchedule,
  ExtendedProgramGuideActionType,
} from 'src/app/core/services/extended-program-guide.service';
import {
  DialogService,
  IDialog,
  IDialogOptions
} from '../../../../shared/secured-shared/dialog/dialog.service';
import { ConfirmationDialogService } from 'src/app/shared/secured-shared/confirmation-dialog/confirmation-dialog.service';
import { DIALOG_FRAME_TEST_IDS } from 'src/app/shared/secured-shared/confirmation-dialog/confirmation-dialog.models';
import { Subscription, firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateTime } from 'luxon';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import TimeParser from 'src/app/core/utils/TimeParser';
import Debounce from 'src/app/core/utils/Debounce';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { EpisodeRecurrenceOptionsComponent } from '../episode-recurrence-options/episode-recurrence-options.component';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import getRecurringScheduleOccurrences, {
  IReplicateRecurringScheduleEpisode
} from '../utils/getRecurringScheduleOccurrences';
import { formatRecurringEpisodeSchedule } from '../extended-program-guide.utils';
import { environment } from 'src/environments/environment';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import {
  IEpisodeDetailsDialogData,
  IEpisodeDetailsDialogViewData,
  IEpisodeDetailsErrors,
  IEpisodeError,
  IEpisodeOccurrence,
  IEpisodeThumbnailInputViewData,
  IRecurringEpisodeViewData,
  getDuration,
  getEpisodesOccurrences,
  setTime,
  validateEpisodeOccurrences
} from 'src/app/core/utils/episode-details-utils';

@Component({
    selector: 'app-episode-details-dialog',
    templateUrl: './episode-details-dialog.component.html',
    styleUrls: ['./episode-details-dialog.component.scss'],
    standalone: false
})
export class EpisodeDetailsDialogComponent
  implements
    OnInit,
    AfterViewInit,
    OnDestroy,
    IDialog<IEpisodeDetailsDialogData, IInputEpisode | null>
{
  public readonly alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  private readonly timeParser = new TimeParser();

  private readonly validationDebounce = new Debounce(100);

  private deleteConfirmationSubscription: Subscription | null = null;

  public close: (result: IInputEpisode | null) => void;

  public data: IEpisodeDetailsDialogData;

  public dialogOptions: IDialogOptions;

  public readonly EpisodeScheduleType = EpisodeScheduleType;

  public dirty = false;

  public titleThumbnailUpdated = false;

  public showThumbnailUpdated = false;

  public tabGroupAnimationDuration: string;

  public previousTab: number = 0;

  public currentTab: number = 0;

  public errors: IEpisodeDetailsErrors = {
    viewerRating: null,
    title: null,
    runsIndefinitely: null,
    description: null,
    showName: null,
    quality: null,
    duration: null,
    start: {
      date: null,
      time: null
    },
    end: {
      date: null,
      time: null
    }
  };

  public isUpdatingViewData = false;

  public showGeneralErrrors = false;

  public showTimeErrrors = false;

  public viewData: IEpisodeDetailsDialogViewData = {
    viewerRating: 0,
    quality: 0,
    title: '',
    formattedRecurrence: null,
    showName: '',
    description: '',
    showDetails: {
      seasonNumber: '',
      episodeNumber: '',
      year: '',
      league: '',
      showType: '',
      closedCaptions: false,
      subtitles: false,
      cast: '',
      host: ''
    },
    duration: '30',
    start: {
      date: DateTime.now().toJSDate(),
      time: '00:00'
    },
    isProcessingThumbnail: false,
    isDraggingOverThumbnailElement: false,
    thumbnails: {
      title: null,
      show: null
    },
    schedule: {
      type: EpisodeScheduleType.Definite,
      end: {
        date: DateTime.now().toJSDate(),
        time: '00:00'
      }
    }
  };

  /**
   * This function needs to be defined as a property, so we can use it as a filter function
   */
  public endDateMaterialDatePickerFilter = (value: Date | null) => {
    if (value === null) {
      return false;
    }
    const startDate = DateTime.fromJSDate(this.viewData.start.date).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    const endDate = DateTime.fromJSDate(value).set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    return endDate.toSeconds() >= startDate.toSeconds();
  };

  /**
   * This function needs to be defined as a property, so we can use it as a filter function
   */
  public startDateMaterialDatePickerFilter = (value: Date | null) => {
    const { schedule } = this.viewData;
    if (value === null || !('end' in schedule)) {
      return true;
    }
    const date = DateTime.fromJSDate(value);
    const today = DateTime.now().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    return date.toSeconds() >= today.toSeconds();
  };

  public constructor(
    public dialog: MatDialog,
    private readonly epgDialog: DialogService,
    private readonly confirmationDialog: ConfirmationDialogService,
    private readonly epgService: ExtendedProgramGuideService,
    private readonly matSnackBar: MatSnackBar,
    private readonly changeDetectionRef: ChangeDetectorRef,
    @Inject(MAT_TABS_CONFIG) public matTabsConfig: MatTabsConfig
  ) {}

  private episodeOccurrences: IEpisodeOccurrence[] = null;

  public ngOnInit(): void {
    this.tabGroupAnimationDuration = environment.test
      ? '0ms'
      : this.matTabsConfig.animationDuration;
    this.dialogOptions.deleteButton.show = this.data.id !== null;
  }

  public ngAfterViewInit(): void {
    this.triggerSetViewData(this.data.episode);
    const replicatedEpisodes = getEpisodesOccurrences(this.data.episodes);
    if ('type' in replicatedEpisodes) {
      return;
    }
    this.episodeOccurrences = replicatedEpisodes;
  }

  public ngOnDestroy(): void {
    if (this.deleteConfirmationSubscription) {
      this.deleteConfirmationSubscription.unsubscribe();
      this.deleteConfirmationSubscription = null;
    }
  }

  public onConfirm(): void {
    if (this.currentTab == 0) {
      this.moveNextTab();
    } else if (this.currentTab == 1) {
      this.isUpdatingViewData = true;
      this.getInputEpisode().then((result) => {
        this.isUpdatingViewData = false;
        if ('message' in result) {
          this.matSnackBar.open(
            `Failed to create episode: ${result.message}`,
            'Ok'
          );
          return;
        }
        this.close(result);
      });
    }
  }

  public onThumbnailTitleUpdateEvent(value: boolean): void {
    this.titleThumbnailUpdated = value;
  }

  public onThumbnailCardUpdateEvent(value: boolean): void {
    this.showThumbnailUpdated = value;
  }

  public onCancel(): void {
    this.close(null);
  }

  public onDelete(): void {
    this.deleteConfirmationSubscription = this.confirmationDialog
      .confirm({
        title: 'Delete Episode Confirmation',
        message: 'Are you sure you want to delete this episode?',
        confirmLabel: 'Delete',
        destructive: true,
        testIds: DIALOG_FRAME_TEST_IDS
      })
      .subscribe({
        next: (value) => {
          if (!value) {
            return;
          }
          this.epgService
            .deleteEpisode(this.data.channelId, this.data.id)
            .subscribe({
              error: () => {
                this.matSnackBar.open('Failed to delete episode', 'Ok');
              },
              next: () => {
                this.matSnackBar.open('Episode successfully deleted', 'Ok');
                this.epgService.broadcastAction({
                  type: ExtendedProgramGuideActionType.DeleteEpisode,
                  episodeId: this.data.id,
                  channelId: this.data.channelId,
                  sender: this.constructor.name
                });
                // ! emit the delete action to be received by the episodes calendar view
                this.close(null);
              }
            });
        }
      });
  }

  public onChangeEndTime(el: HTMLInputElement) {
    this.setEndTime(el.value);
  }

  public onChangeDuration(e: HTMLInputElement) {
    this.setDuration(e.value);
    this.onViewDataChange();
  }

  public onChangeRunsIndefinitely(e: MatCheckboxChange) {
    if (e.checked) {
      this.viewData.schedule = {
        type: EpisodeScheduleType.Indefinite
      };
    } else {
      /**
       * We can put anything here since `duration` will be updated by `calculateEndTimeFromDuration` call
       */
      this.viewData.schedule = {
        type: EpisodeScheduleType.Definite,
        end: {
          date: new Date(0),
          time: '00:00'
        }
      };
    }
    this.onViewDataChange();
  }

  /**
   * This function should only be called when the episode details form is valid
   * @returns @type {IInputEpisode} if the input is valid, an error message otherwise
   */
  private async getInputEpisode(): Promise<
    | IInputEpisode
    | {
        message: string;
      }
  > {
    const {
      viewerRating,
      showName,
      description,
      duration,
      start,
      schedule,
      title
    } = this.viewData;
    let newSchedule: EpisodeSchedule;
    switch (schedule.type) {
      case EpisodeScheduleType.Definite:
        newSchedule = {
          type: EpisodeScheduleType.Definite
        };
        break;
      case EpisodeScheduleType.Recurring:
        newSchedule = {
          type: EpisodeScheduleType.Recurring,
          recurrence: schedule.recurrence,
          end_date: DateTime.fromJSDate(schedule.end.date).toSeconds()
        };
        break;
      case EpisodeScheduleType.Indefinite:
        newSchedule = {
          type: EpisodeScheduleType.Indefinite
        };
        break;
    }
    const uploadedThumbnails: {
      show: string | null;
      title: string | null;
    } = {
      show: null,
      title: null
    };
    const viewData = this.viewData;
    let updated = {
      show: this.showThumbnailUpdated,
      title: this.titleThumbnailUpdated
    };
    let initial = {
      show: this.data?.episode?.show_thumbnail_url,
      title: this.data?.episode?.title_thumbnail_url
    };
    for (const key of ['show', 'title'] as const) {
      if (viewData.thumbnails[key] === null) {
        continue;
      }
      try {
        if (updated[key] === false) {
          uploadedThumbnails[key] = initial[key];
          continue;
        }
        let fileName = viewData.thumbnails[key].file?.name;
        let form = new FormData();
        form.append('file', viewData.thumbnails[key].file, fileName);
        const res = await firstValueFrom(
          this.epgService.uploadEpisodeThumbnail(form)
        );
        uploadedThumbnails[key] = res.filepath;
      } catch (reason) {
        return {
          message: `Failed to upload ${key}: ${reason}`
        };
      }
    }
    const startDate = DateTime.fromJSDate(start.date);
    return {
      starRating: null,
      options: {
        episode: null,
        game: null,
        host: this.viewData.showDetails.host,
        hasSubtitles: this.viewData.showDetails.subtitles,
        hasClosedCaptions: this.viewData.showDetails.closedCaptions
      },
      schedule: newSchedule,
      show_type: this.viewData.showDetails.showType,
      quality: this.viewData.quality,
      start_date: startDate.toSeconds(),
      duration: parseInt(duration, 10),
      viewer_rating: `+${viewerRating}`,
      title,
      name: showName,
      description,
      show_thumbnail_url: uploadedThumbnails.show,
      title_thumbnail_url: uploadedThumbnails.title
    };
  }

  public onChangeStartTime(el: HTMLInputElement, _: Event) {
    this.setStartTime(el.value);
    this.onViewDataChange();
  }

  public onChangeEndDate(el: Date | MatDatepickerInputEvent<Date>) {
    let newDate: Date | null = 'value' in el ? el.value : el;
    if (newDate === null) {
      this.errors.end.date = 'End date can be selected using the date picker';
    } else {
      this.errors.end.date = null;
      this.setEndDate(newDate);
    }
    this.onViewDataChange();
  }

  public onChangeStartDate(el: Date | MatDatepickerInputEvent<Date>) {
    let newDate: Date | null = 'value' in el ? el.value : el;
    if (newDate === null) {
      this.errors.start.date =
        'Start date can be selected using the date picker';
    } else {
      this.errors.start.date = null;
      this.setStartDate(newDate);
    }
    this.onViewDataChange();
  }

  /**
   * Update component `viewData` property from the input episode
   * @param value Input episode
   */
  private async setViewData(value: IInputEpisode) {
    /**
     * Quality
     */
    this.viewData.quality = value.quality;

    /**
     * Description
     */
    this.viewData.description = value.description;

    /**
     * Start time
     */
    const startTime = DateTime.fromSeconds(value.start_date);
    this.viewData.start.date = startTime.toJSDate();
    this.viewData.start.time = startTime.toFormat('hh:mm a');

    /**
     * Closed captions
     */
    this.viewData.showDetails.closedCaptions = value.options.hasClosedCaptions;

    /**
     * Viewer rating
     */
    this.viewData.viewerRating = parseInt(value.viewer_rating, 10);

    /**
     * Duration
     */
    this.viewData.duration = value.duration.toString();

    /**
     * Title
     */
    this.viewData.title = value.title;

    /**
     * Set schedule information
     */
    const schedule = value.schedule;
    switch (schedule.type) {
      case EpisodeScheduleType.Definite: {
        const endDate = DateTime.fromSeconds(value.start_date).plus({
          minutes: value.duration
        });
        this.viewData.schedule = {
          type: EpisodeScheduleType.Definite,
          end: {
            date: endDate.toJSDate(),
            time: endDate.toFormat('hh:mm a')
          }
        };
        break;
      }
      case EpisodeScheduleType.Recurring:
        this.viewData.schedule = {
          type: EpisodeScheduleType.Recurring,
          recurrence: schedule.recurrence,
          end: {
            date: DateTime.fromSeconds(schedule.end_date).toJSDate(),
            time: DateTime.fromSeconds(schedule.end_date).toFormat('hh:mm a')
          }
        };
        break;
      case EpisodeScheduleType.Indefinite:
        this.viewData.schedule = {
          type: EpisodeScheduleType.Indefinite
        };
        break;
    }
    return this.triggerUpdate().then(() => null);
  }

  private formatRecurringEpisodeSchedule(
    recurringData: IRecurringEpisodeViewData,
    startDate: Date,
    startTime: string
  ) {
    return formatRecurringEpisodeSchedule(recurringData, startDate, startTime);
  }

  public onChangeEpisodeSchedule(
    newSchedule: IEpisodeDetailsDialogViewData['schedule']
  ) {
    this.viewData.schedule = newSchedule;
    this.onViewDataChange();
    switch (newSchedule.type) {
      case EpisodeScheduleType.Definite:
      case EpisodeScheduleType.Indefinite:
        break;
      case EpisodeScheduleType.Recurring: {
        const duration = getDuration(this.viewData.duration);
        if (typeof duration === 'string') {
          break;
        }
        const recurringEpisodeSchedule: IReplicateRecurringScheduleEpisode = {
          duration,
          start_date: DateTime.fromJSDate(this.viewData.start.date).toSeconds()
        };
        const episodeOccurrences = getRecurringScheduleOccurrences(
          recurringEpisodeSchedule,
          {
            type: EpisodeScheduleType.Recurring,
            end_date: DateTime.fromJSDate(newSchedule.end.date).toSeconds(),
            recurrence: newSchedule.recurrence
          }
        );
        if ('type' in episodeOccurrences) {
          console.log(episodeOccurrences);
          break;
        }
        for (const occurrence of episodeOccurrences) {
          console.log(
            'start = %s, end = %s',
            occurrence.startDate.toLocaleString(DateTime.DATETIME_FULL),
            occurrence.endDate.toLocaleString(DateTime.DATETIME_FULL)
          );
        }
        console.log(
          validateEpisodeOccurrences(
            this.episodeOccurrences,
            recurringEpisodeSchedule
          )
        );
        break;
      }
    }
  }

  public onEpisodeScheduleTypeSelectionChange(
    event: MatSelectChange,
    el: MatSelect
  ) {
    switch (event.value as EpisodeScheduleType) {
      case EpisodeScheduleType.Definite:
        this.onChangeEpisodeSchedule({
          type: EpisodeScheduleType.Definite,
          end: {
            date: DateTime.now().toJSDate(),
            time: '00:00'
          }
        });
        break;
      case EpisodeScheduleType.Recurring:
        el.value = EpisodeScheduleType.Definite;
        this.openSetEpisodeRecurrenceDialog();
        break;
      /**
       * Does nothing, this option should be disabled
       */
      case EpisodeScheduleType.Indefinite:
        break;
    }
  }

  public onRecurringOptionClick() {
    if (this.viewData.schedule.type === EpisodeScheduleType.Recurring) {
      this.openSetEpisodeRecurrenceDialog();
    }
  }

  public onChangeTitleThumbnail(
    thumbnail: IEpisodeThumbnailInputViewData | null
  ) {
    this.viewData.thumbnails.title = thumbnail;
    this.onViewDataChange();
  }

  public onChangeShowThumbnail(
    thumbnail: IEpisodeThumbnailInputViewData | null
  ) {
    this.viewData.thumbnails.show = thumbnail;
    this.onViewDataChange();
  }

  public onViewDataChange() {
    this.triggerUpdate().then(() => {
      this.dirty = true;
    });
  }

  /**
   * This should be called whenever a view data is changed, so we can disable the save button,
   * and only enable it if everything is gtg.
   *
   * The promise returned will only be resolved after the current debounce timer is fired. Meaning,
   * if it is cleared, the promise will never be resolved.
   */
  private triggerUpdate() {
    /**
     * disable the save button until the update is completed
     */
    this.dialogOptions.saveButton.disable = false;
    if (this.currentTab == 0) {
      this.dialogOptions.saveButton.label = 'Next';
    } else {
      this.dialogOptions.saveButton.label = 'Save';
    }
    return this.validationDebounce.run(() => {
      this.update();
      this.changeDetectionRef.detectChanges();
    });
  }

  private openSetEpisodeRecurrenceDialog() {
    const startDate = DateTime.fromJSDate(this.viewData.start.date);
    const duration = getDuration(this.viewData.duration);

    if (typeof duration === 'string') {
      this.matSnackBar.open('Invalid duration', 'Ok');
      return;
    }

    this.epgDialog
      .openDialog({
        title: 'Set Recurrence',
        content: EpisodeRecurrenceOptionsComponent,
        data: {
          startDate: startDate.toJSDate(),
          startTime: this.viewData.start.time,
          duration,
          episodes: this.data.episodes,
          schedule: {
            type: EpisodeScheduleType.Recurring,
            end: {
              date: DateTime.now().plus({ weeks: 2 }).toJSDate(),
              time: '00:00'
            },
            recurrence: {
              type: RecurrenceType.Week,
              week_days: [startDate.weekday],
              repeat: 1
            }
          }
        }
      })
      .afterClosed()
      .subscribe((selectedOptions) => {
        if (selectedOptions === null) {
          return;
        }
        this.viewData.start.date = selectedOptions.startDate;
        this.onChangeEpisodeSchedule(selectedOptions.schedule);
      });
  }

  private formatEpisode(title: string, startDate: DateTime, endDate: DateTime) {
    return `${title} starts at ${startDate.toLocaleString(
      DateTime.DATETIME_FULL
    )}, and ends at ${endDate.toLocaleString(DateTime.DATETIME_FULL)}`;
  }

  private validateOverlappingEpisodes() {
    const schedule = this.viewData.schedule;
    switch (schedule.type) {
      case EpisodeScheduleType.Definite: {
        /**
         * Validate start time of the definite episodes
         */
        const startDate = DateTime.fromJSDate(this.viewData.start.date);
        const endDate = DateTime.fromJSDate(schedule.end.date);
        existingEpisodesLoop: for (const ep of this.data.episodes) {
          switch (ep.schedule.type) {
            case EpisodeScheduleType.Definite:
              const existingEpisodeStartDate = DateTime.fromSeconds(
                ep.start_date
              );
              const existingEpisodeEndDate = existingEpisodeStartDate.plus({
                minutes: ep.duration
              });
              const existingEpisodeReadableRange = this.formatEpisode(
                ep.title,
                existingEpisodeStartDate,
                existingEpisodeEndDate
              );
              if (
                startDate.toSeconds() <= existingEpisodeEndDate.toSeconds() &&
                startDate.toSeconds() >= existingEpisodeStartDate.toSeconds()
              ) {
                this.errors.start.time = `This episode start time overlaps with another episode: ${existingEpisodeReadableRange}`;
                break existingEpisodesLoop;
              } else if (
                endDate.toSeconds() >= existingEpisodeStartDate.toSeconds() &&
                startDate.toSeconds() <= existingEpisodeStartDate.toSeconds()
              ) {
                this.errors.duration = `This episode end time overlaps with another episode: ${existingEpisodeReadableRange}`;
                break existingEpisodesLoop;
              }
              break;
            case EpisodeScheduleType.Recurring:
            case EpisodeScheduleType.Indefinite:
              break;
          }
        }
        break;
      }
      case EpisodeScheduleType.Recurring:
        // ! add support for validating recurring episodes
        break;
      case EpisodeScheduleType.Indefinite:
        for (const ep of this.data.episodes) {
          if (ep.schedule.type === EpisodeScheduleType.Indefinite) {
            this.errors.runsIndefinitely =
              'There is already an indefinite episode. Only one indefinite episode per channel is allowed';
            break;
          }
        }
    }
  }

  /**
   * Set recurrence formatted string if the episode is recurring
   */
  private setRecurrenceFormattedString() {
    const schedule = this.viewData.schedule;
    if (schedule.type !== EpisodeScheduleType.Recurring) {
      this.viewData.formattedRecurrence = null;
      return;
    }
    this.viewData.formattedRecurrence = this.formatRecurringEpisodeSchedule(
      schedule,
      this.viewData.start.date,
      this.viewData.start.time
    );
  }

  private setDuration(value: string) {
    const n = getDuration(value);
    if (typeof n === 'string') {
      this.errors.duration = n;
      this.viewData.duration = value;
      return null;
    } else {
      this.errors.duration = null;
      this.viewData.duration = n.toString();
      this.calculateEndTimeFromDuration(n);
    }
    return n;
  }

  private setStartDate(value: Date) {
    const { schedule } = this.viewData;
    const viewData = this.viewData;
    const parsedStartTime = this.timeParser.parse(viewData.start.time);
    this.viewData.start.date = value;
    if ('message' in parsedStartTime) {
      /**
       * The caller of setStartTime should do the validation
       */
      return;
    }
    const startDate = setTime(
      DateTime.fromJSDate(viewData.start.date),
      parsedStartTime
    );
    viewData.start.date = startDate.toJSDate();
    if (!('end' in schedule)) {
      console.error('setStartDate: Unexpected schedule type: %o', schedule);
      return;
    }
    // Keep the previous duration
    const n = getDuration(this.viewData.duration);
    if (typeof n === 'string') {
      return;
    } else {
      this.calculateEndTimeFromDuration(n);
    }
    this.updateSaveButtonState();
  }

  private setStartTime(value: string) {
    const { schedule } = this.viewData;
    const viewData = this.viewData;
    viewData.start.time = value;
    const parsed = this.timeParser.parse(value);
    if ('message' in parsed) {
      this.errors.start.time = parsed.message;
    } else {
      /**
       * Update the time of start date object
       */
      const startDate = setTime(
        DateTime.fromJSDate(viewData.start.date),
        parsed
      );
      this.errors.start.time = null;
      viewData.start.date = startDate.toJSDate();
      if ('end' in schedule) {
        // Keep the previous duration
        const n = getDuration(this.viewData.duration);
        if (typeof n === 'string') {
          return;
        } else {
          this.calculateEndTimeFromDuration(n);
        }
      }
    }
    this.updateSaveButtonState();
  }

  private setEndTime(value: string) {
    const { schedule } = this.viewData;
    if (!('end' in schedule)) {
      return;
    }
    schedule.end.time = value;
    /**
     * Parse new end time
     */
    const parsedEndTime = this.timeParser.parse(value);
    /**
     * Set end time error
     */
    if ('message' in parsedEndTime) {
      this.errors.end.time = parsedEndTime.message;
    } else {
      this.errors.end.time = null;
      /**
       * Set the time of the end date
       */
      const endDate = setTime(
        DateTime.fromJSDate(schedule.end.date),
        parsedEndTime
      );
      /**
       * Update end date time
       */
      schedule.end.date = endDate.toJSDate();
      const duration = endDate.diff(
        DateTime.fromJSDate(this.viewData.start.date),
        'minutes'
      ).minutes;
      if (duration < 0) {
        this.errors.end.time = 'End time should be greater than start date';
      } else {
        this.errors.end.time = null;
        /**
         * Recalculate duration based on new end time
         */
        this.setDuration(duration.toString());
        /**
         * Set end time again, since `setDuration` might call `calculateEndTimeFromDuration`.
         */
        schedule.end.time = value;
      }
    }

    this.updateSaveButtonState();
  }

  private setEndDate(date: Date) {
    const { schedule } = this.viewData;

    if (schedule.type !== EpisodeScheduleType.Definite) {
      return;
    }

    const endTime = this.timeParser.parse(schedule.end.time);

    if ('message' in endTime) {
      this.errors.end.time = endTime.message;
    } else {
      const endDate = setTime(DateTime.fromJSDate(date), endTime);
      schedule.end.date = endDate.toJSDate();
      const duration = endDate.diff(
        DateTime.fromJSDate(this.viewData.start.date),
        'minutes'
      ).minutes;

      if (duration < 0.0) {
        this.errors.end.date = 'End date should be greater than start date';
      } else {
        this.errors.end.date = null;

        /**
         * Set the new duration based on the selected end date
         */
        this.setDuration(duration.toString());
      }
    }

    this.updateSaveButtonState();
  }

  private update() {
    this.updateSaveButtonState();
  }

  updateViewDataFromChilds(event: IEpisodeDetailsDialogViewData) {
    const merged: IEpisodeDetailsDialogViewData = {};
    Object.keys(this.viewData).forEach(
      (key) => (merged[key] = event[key] ? event[key] : this.viewData[key])
    );
    this.viewData = merged;
  }

  updateErrorFromChilds(event: IEpisodeDetailsErrors) {
    const merged: IEpisodeDetailsErrors = {};
    Object.keys(this.errors).forEach((key) => (merged[key] = event[key]));
    this.errors = merged;
  }

  /**
   * Update whether we should disable the save button or not, based
   * on whether we have errors or not
   */
  private updateSaveButtonState() {
    if (this.currentTab == 0) {
      this.dialogOptions.saveButton.disable = false;
    } else {
      this.dialogOptions.saveButton.disable =
        this.errors.description !== null ||
        this.errors.title !== null ||
        this.errors.showName !== null ||
        this.errors.quality !== null ||
        this.errors.start.time !== null ||
        this.errors.start.date !== null ||
        this.errors.end.time !== null ||
        this.errors.end.date !== null ||
        this.errors.duration !== null ||
        this.errors.viewerRating !== null ||
        this.viewData.isProcessingThumbnail ||
        this.viewData.isDraggingOverThumbnailElement;
    }
  }

  private calculateEndTimeFromDuration(duration: number) {
    const { start, schedule } = this.viewData;
    switch (schedule.type) {
      case EpisodeScheduleType.Recurring:
      case EpisodeScheduleType.Definite: {
        const parsedStartTime = this.timeParser.parse(start.time);
        if ('message' in parsedStartTime) {
          break;
        }
        /**
         * Parse end time so we can keep the same format for it
         */
        const parsedEndTime = this.timeParser.parse(schedule.end.time);
        /**
         * We cannot check the format if it has parsing error
         */
        if ('message' in parsedEndTime) {
          break;
        }
        const startDate = setTime(
          DateTime.fromJSDate(start.date),
          parsedStartTime
        );
        const endDate = startDate.plus({
          minutes: duration
        });
        schedule.end.date = endDate.toJSDate();
        schedule.end.time = endDate.toFormat(
          /**
           * If there is no meridiem set, then it's using 24-hour format, otherwise,
           * it's using 12-hour format. Either way, we just stick to it.
           */
          parsedEndTime.meridiem === null ? 'HH:mm' : 'hh:mm a'
        );
        break;
      }
      case EpisodeScheduleType.Indefinite:
        break;
    }
  }

  private triggerSetViewData(value: IInputEpisode) {
    if (this.isUpdatingViewData) {
      return;
    }
    this.isUpdatingViewData = true;
    this.setViewData(value)
      .catch(
        (err): IEpisodeError => ({
          message: `${err}`
        })
      )
      .then((err) => {
        if (err !== null) {
          this.matSnackBar.open(
            `Failed to process episode information: ${err.message}`,
            'Ok'
          );
        }
      })
      .finally(() => {
        this.isUpdatingViewData = false;
      });
  }

  onTabChanged(event: number) {
    this.currentTab = event;
    if (!this.validateCurrentTab()) {
      this.showTabErrors();
    }
    this.previousTab = event;
    this.onViewDataChange();
  }

  showTabErrors() {
    if (this.currentTab == 0) {
      this.showGeneralErrrors = true;
    } else {
      this.showTimeErrrors = true;
    }
  }

  moveNextTab() {
    this.previousTab = this.currentTab;
    this.currentTab = this.currentTab + 1;
  }

  validateCurrentTab() {
    let result = false;
    switch (this.previousTab) {
      case 0:
        if (!this.validateGeneral()) {
          this.showMsgDialog(
            'The previous tab has errors, please solve them first.',
            this.previousTab
          );
        } else {
          result = true;
        }
        break;
      case 1:
        if (!this.validateTime()) {
          this.showMsgDialog(
            'The previous tab has errors, please solve them first.',
            this.previousTab
          );
        } else {
          result = true;
        }
        break;
      default:
        break;
    }
    return result;
  }

  validateGeneral() {
    let result =
      !this.errors.description &&
      !this.errors.title &&
      !this.errors.showName &&
      !this.errors.quality &&
      !this.errors.viewerRating;
    return result;
  }

  validateTime() {
    let result = true;
    return result;
  }

  showMsgDialog(message: string, prevTab?: number): void {
    this.confirmationDialog.notice(message).subscribe(() => {
      this.currentTab = prevTab;
    });
  }

  toggleClosedCaptions(event) {
    this.viewData.showDetails.closedCaptions = event.checked;
  }
}
